import moment from 'moment-jalaali';
import messages from './Messages';
import {
  englishNumber,
  isANumber,
  convertDigitToProperFormat,
} from 'utils/number';
import { monthIndexToTimestamp } from 'utils/date';
import {
  calculateValue,
  addScoreToMetric,
  calculatePercentage,
} from 'components/Chart/analyseUtils';
import { IQueryBucket } from 'store/entity/IQueryBucket';
import { IQueryMetric } from 'store/entity/IQueryMetric';
import { IAnalyticResponse } from 'store/entity/IAnalyticResponse';
import { IQueryFilter } from 'store/entity/IQueryFilter';
import cloneDeep from 'lodash/cloneDeep';
import { IUser, IUsers } from 'store/users/usersType';
import {
  ICheckPoint,
  IChoiceView,
  IGridQuestionView,
  IMultipleAnswerQuestionView,
  IMultipleChoiceQuestionView,
  IQuestionReportField,
  IQuestionsView,
  ISeries,
  ITagReportField,
  TimeScale,
} from 'store/survey/surveyTypes';
import { IProvider, ITags, ITagView } from 'store/providers/providerTypes';
import i18n, { getCalendarLocal } from '../../i18n';
import { ILocal } from './BaseDatePicker';
import { smartNumericCompare } from '../../utils/string';

moment.loadPersian({ dialect: 'persian-modern', usePersianDigits: true });
const totalKey = '__total';
export interface IResponseBucket {
  [key: string]: string | number | IResponseBucket;
}

export interface IAnswerItem {
  __name: string;
  __value?: number | IAnswerItem[];
  __value__n?: number;
  __value__d?: number;
  __type?: 'value' | 'percent';
  __target?: number;
  __baseLine?: number;
  __percentageOfRealization?: number;
  __id: number | string;
}

export interface IQuestionChoice {
  id: number | string;
  value: string;
  order?: number;
}

export interface IContext {
  tags: ITags;
  questions: {
    [questionId: number]: IQuestionsView;
  };
  timeScale?: TimeScale;
  surveyMaxScore?: number;
  providers?: any;
  users?: IUsers['map'];
  checkPoints: ICheckPoint[];
  fields: Array<IQuestionReportField | ITagReportField>;
  local: string;
}

abstract class Bucket {
  queryBucket: IQueryBucket;
  queryMetric: IQueryMetric;
  queryFilters: IQueryFilter[];
  analyse: IAnalyticResponse;
  series: ISeries;
  context: IContext;
  constructor(
    queryBucket: IQueryBucket,
    queryMetric: IQueryMetric,
    queryFilters: IQueryFilter[],
    analyse: IAnalyticResponse,
    series: ISeries,
    context: IContext
  ) {
    this.queryBucket = queryBucket;
    this.queryMetric = queryMetric;
    this.queryFilters = queryFilters;
    this.analyse = analyse;
    this.series = series;
    this.context = context;
  }
  static bucketOf = (
    queryBucket: IQueryBucket,
    queryMetric: IQueryMetric,
    queryFilters: IQueryFilter[],
    analyse: IAnalyticResponse,
    series: ISeries,
    context: IContext
  ): Bucket => {
    if (queryBucket) {
      if (queryBucket.name === 'TermByFieldValue') {
        return new MultipleAnswerQuestionFieldsBucket(
          queryBucket,
          queryMetric,
          queryFilters,
          analyse,
          series,
          context
        );
      } else if (
        queryBucket.field === 'tags' ||
        queryBucket.field === 'q_tags'
      ) {
        return new TagsBucket(
          queryBucket,
          queryMetric,
          queryFilters,
          analyse,
          series,
          context
        );
      } else if (queryBucket.field === 'allAnswers') {
        return new QuestionAnswersBucket(
          queryBucket,
          queryMetric,
          queryFilters,
          analyse,
          series,
          context
        );
      } else if (queryBucket.field === 'value') {
        return new QuestionAnswersBucket(
          queryBucket,
          queryMetric,
          queryFilters,
          analyse,
          series,
          context
        );
      } else if (
        queryBucket.field === 'effectiveDate' ||
        queryBucket.field === 'effectiveJMonth'
      ) {
        return new TimeBucket(
          queryBucket,
          queryMetric,
          queryFilters,
          analyse,
          series,
          context
        );
      } else if (queryBucket.field === 'questionId') {
        return new AllQuestionsBucket(
          queryBucket,
          queryMetric,
          queryFilters,
          analyse,
          series,
          context
        );
      } else if (queryBucket.field === 'sqId') {
        return new ServerTitledBucket(
          queryBucket,
          queryMetric,
          queryFilters,
          analyse,
          series,
          context
        );
      } else if (queryBucket.field === 'subQuestionId') {
        return new AllSubQuestionsBucket(
          queryBucket,
          queryMetric,
          queryFilters,
          analyse,
          series,
          context
        );
      } else if (queryBucket.field === 'instance') {
        return new InstanceBucket(
          queryBucket,
          queryMetric,
          queryFilters,
          analyse,
          series,
          context
        );
      }
      throw Error();
    } else {
      return new WithoutBucket(
        queryBucket,
        queryMetric,
        queryFilters,
        analyse,
        series,
        context
      );
    }
  };
  abstract extractResponse(
    category?: IAnalyticResponse['categories'],
    checkPoint?: ICheckPoint | undefined
  ): IAnswerItem[];
}

class WithoutBucket extends Bucket {
  extractResponse(_category?: IAnalyticResponse['categories']): IAnswerItem[] {
    const { metric } = this.series;
    const name = messages.generateMetricName(metric);
    const value = calculateValue(
      this.analyse.metrics ? this.analyse.metrics[this.queryMetric.name] : 0
    );
    const answer: IAnswerItem = {
      __name: name,
      __id: 1,
      __value: value,
    };
    return [answer];
  }
}

class TagsBucket extends Bucket {
  extractResponse(category?: IAnalyticResponse['categories']): IAnswerItem[] {
    let tagsIds = cloneDeep(this.queryBucket.validBuckets!);
    tagsIds = tagsIds?.sort(
      (a, b) =>
        (this.context.tags?.data?.[a].order ?? 0) -
        (this.context.tags?.data?.[b].order ?? 0)
    );
    const ans: IAnswerItem[] = [];

    if (this.queryBucket.includeTotal) {
      tagsIds = [...tagsIds, totalKey];
    }

    tagsIds.forEach((tagId) => {
      let item: IAnswerItem;
      if (tagId !== totalKey) {
        const tag = this.context.tags?.data?.[tagId];
        item = {
          __name: tag?.text || tagId.toString(),
          __value: this.queryMetric.metric === 'count' ? 0 : undefined,
          __id: tagId,
        };
      } else {
        item = {
          __name: messages.total,
          __value: this.queryMetric.metric === 'count' ? 0 : undefined,
          __id: totalKey,
        };
      }

      if (
        this.queryBucket.subBuckets &&
        this.queryBucket.subBuckets.length > 0
      ) {
        const bucket = Bucket.bucketOf(
          this.queryBucket.subBuckets[0],
          this.queryMetric,
          this.queryFilters,
          this.analyse,
          this.series,
          this.context
        );
        let subCategory: IAnalyticResponse['categories'] | undefined;
        if (category && category[this.queryBucket.name]) {
          const foundBucket = category[this.queryBucket.name].buckets.find(
            (b) => Number(b.val) === tagId || b.val === tagId
          );
          if (foundBucket) {
            subCategory = foundBucket.categories;
            item.__value = bucket.extractResponse(subCategory);
          } else if (this.queryBucket.hideMissingData) {
            return;
          }
        } else if (this.queryBucket.hideMissingData) {
          return;
        }
      } else {
        if (category) {
          const foundBucket = category[this.queryBucket.name].buckets.find(
            (bucket) => bucket.val === tagId.toString()
          );
          if (foundBucket) {
            if (
              foundBucket.count === 0 &&
              this.queryMetric.metric !== 'count'
            ) {
              item.__value = undefined;
            } else {
              const { metric } = this.series;
              const metricScore = addScoreToMetric(metric);
              fillItemRawValues(item, foundBucket, metricScore);
            }
          } else if (this.queryBucket.hideMissingData) {
            return;
          } else if (this.queryMetric.metric !== 'count') {
            item.__value = undefined;
          }
        } else if (this.queryBucket.hideMissingData) {
          return;
        }
      }
      ans.push(item);
    });

    return ans;
  }
}

class QuestionAnswersBucket extends Bucket {
  extractResponse(
    category?: IAnalyticResponse['categories'],
    checkPoint?: ICheckPoint | undefined
  ): IAnswerItem[] {
    const ans: IAnswerItem[] = [];

    const questionId =
      this.queryBucket.customField ||
      (this.queryBucket.prefix as string).split('$$')[0];
    const question = this.context.questions[questionId];
    if (!question) return ans;
    let questionChoices = this.getQuestionChoices(
      question as IMultipleChoiceQuestionView
    );
    // if user choose a question choice in filters, we need to show just these choices
    const allAnswersFilter = this.queryFilters.find(
      (filter) =>
        filter.field === 'allAnswers' &&
        !!filter.values &&
        (filter.values as string[]).some(
          (value) => !!value.includes(questionId)
        )
    );
    if (allAnswersFilter && !question.goDeepInTags) {
      questionChoices = questionChoices.filter(
        (choice) =>
          allAnswersFilter.values &&
          (allAnswersFilter.values as string[]).includes(
            `${question.id}$$${choice.id}`
          )
      );
    }
    if (this.queryBucket.includeTotal) {
      questionChoices.push({ id: totalKey, value: messages.total });
    }
    questionChoices.forEach((choice: IQuestionChoice) => {
      const item: IAnswerItem = {
        __name: choice.value,
        __value: this.queryMetric.metric === 'count' ? 0 : undefined,
        __id: choice.id,
      };

      if (
        this.queryBucket.subBuckets &&
        this.queryBucket.subBuckets.length > 0
      ) {
        const bucket = Bucket.bucketOf(
          this.queryBucket.subBuckets[0],
          this.queryMetric,
          this.queryFilters,
          this.analyse,
          this.series,
          this.context
        );
        let subCategory: IAnalyticResponse['categories'] | undefined;
        item.__value = bucket.extractResponse(subCategory); // add value for subbucket for default to show zero or undefined in chart
        if (category && category[this.queryBucket.name]) {
          const foundBucket = category[this.queryBucket.name].buckets.find(
            (b) =>
              b.val === choice.id ||
              b.val === `${this.queryBucket.prefix}$$${choice.id}`
          );
          if (foundBucket) {
            subCategory = foundBucket.categories;
            if (
              foundBucket.count === 0 &&
              this.queryMetric.metric !== 'count'
            ) {
              item.__value = undefined;
            } else {
              item.__value = bucket.extractResponse(subCategory) || 0;
            }
          } else if (this.queryBucket.hideMissingData) {
            return;
          }
        }
      } else {
        if (category && category[this.queryBucket.name].buckets) {
          const foundBucket = category[this.queryBucket.name].buckets.find(
            (bucket) =>
              bucket.val.toString() === choice.id.toString() ||
              bucket.val === `${this.queryBucket.prefix}$$${choice.id}`
          );

          if (foundBucket) {
            const { metric } = this.series;
            if (
              foundBucket.count === 0 &&
              metric !== 'count' &&
              metric !== 'percentageOfCount'
            ) {
              item.__value = undefined;
            } else {
              const { metric } = this.series;
              const metricScore = addScoreToMetric(metric);
              if (metric === 'percentageOfCount') {
                if (foundBucket.val === '__total') {
                  /*don't add total value in case of percentageOfCount as it's always 100%*/
                  return;
                } else {
                  const totalBucketValue =
                    category[this.queryBucket.name].buckets.find(
                      (bucket) => bucket.val === '__total'
                    )?.metrics[metricScore] || 1;
                  item.__value =
                    calculateValue(
                      (foundBucket.metrics[metricScore] * 100) /
                        totalBucketValue
                    ) || 0;
                  item.__value__n = foundBucket.metrics[metricScore] * 100;
                  item.__value__d = totalBucketValue;
                  item.__type = 'percent';
                }
              } else fillItemRawValues(item, foundBucket, metricScore, 0);
            }
          } else if (this.queryBucket.hideMissingData) {
            return;
          } else if (this.queryMetric.metric !== 'count') {
            item.__value = undefined;
          } else if (
            this.series.metric === 'percentageOfCount' &&
            choice.id === '__total'
          ) {
            /*don't add total value in case of percentageOfCount as it's always 100%*/
            return;
          }
        } else if (this.queryBucket.hideMissingData) {
          return;
        }
      }
      if (
        checkPoint &&
        isANumber(checkPoint.target) &&
        isANumber(checkPoint.baseLine)
      ) {
        let appliedTarget = checkPoint.target as number;
        let appliedBaseLine = checkPoint.baseLine as number;
        if (checkPoint.properties?.distinctiveValues?.[choice.id.toString()]) {
          appliedTarget = checkPoint.properties?.distinctiveValues[
            choice.id.toString()
          ].target as number;
          appliedBaseLine = checkPoint.properties?.distinctiveValues[
            choice.id.toString()
          ].baseLine as number;
        }
        item.__target = appliedTarget;
        item.__baseLine = appliedBaseLine;
        if (typeof item.__value === 'number') {
          item.__percentageOfRealization = calculatePercentage(
            item.__value,
            item.__target,
            item.__baseLine
          );
        }
      }
      ans.push(item);
    });
    return ans;
  }

  bfsOverTagsOnDepth(
    parentTagId: number,
    depth: number,
    acc: IQuestionChoice[]
  ) {
    const currentTag = Object.values(this.context.tags?.data).find(
      (tag) => tag.id === parentTagId
    );

    const tagList = Object.values(this.context.tags?.data).filter(
      (tag) => tag.parentId === parentTagId
    );
    if (depth === this.queryBucket.depth) {
      return [
        ...acc,
        {
          id: currentTag!.id,
          value: currentTag!.text,
          order: currentTag!.order,
        },
      ];
    } else {
      let result: IQuestionChoice[] = [];
      tagList?.forEach((tag) => {
        result = [
          ...result,
          ...this.bfsOverTagsOnDepth(Number(tag.id), depth + 1, acc),
        ];
      });
      return result;
    }
  }

  bfsOverTagsTillDepth(
    parentTagId: number,
    depth: number,
    maxDepth: number,
    acc: IQuestionChoice[]
  ) {
    const currentTag = Object.values(this.context.tags?.data).find(
      (tag) => tag.id === parentTagId
    );

    const tagList = Object.values(this.context.tags?.data).filter(
      (tag) => tag.parentId === parentTagId
    );

    if (depth === maxDepth) {
      return [
        ...acc,
        {
          id: currentTag!.id,
          value: currentTag!.text,
          order: currentTag!.order,
        },
      ];
    } else {
      let result: IQuestionChoice[] = [
        {
          id: currentTag!.id,
          value: currentTag!.text,
          order: currentTag!.order,
        },
      ];
      for (const tag of tagList) {
        result = [
          ...result,
          ...this.bfsOverTagsTillDepth(
            Number(tag.id),
            depth + 1,
            maxDepth,
            acc
          ),
        ];
      }
      return result;
    }
  }

  getQuestionChoices = (
    question: IMultipleChoiceQuestionView
  ): IQuestionChoice[] => {
    let list: IQuestionChoice[] = [];
    if (question.useTags) {
      const tagChoices: IQuestionChoice[] = question.tagChoices
        ? isANumber(this.queryBucket.depth)
          ? question.tagChoices.reduce(
              (acc: IQuestionChoice[], tagId: number) => {
                const newAcc = [
                  ...acc,
                  ...this.bfsOverTagsOnDepth(tagId, 0, []),
                ];
                return newAcc;
              },
              []
            )
          : question.tagChoices.reduce(
              (acc: IQuestionChoice[], tagId: number) => {
                const newAcc = [
                  ...acc,
                  ...this.bfsOverTagsTillDepth(
                    tagId,
                    0,
                    question.goDeepInTags ?? 0,
                    []
                  ),
                ];
                return newAcc;
              },
              []
            )
        : [];
      const parentTagChoices: IQuestionChoice[] = question.parentTagChoices
        ? question.parentTagChoices.reduce(
            (childTags: IQuestionChoice[], tagId: number) => {
              const excludedTagChoices = question.excludedTagChoices;
              Object.values(this.context.tags.data || {})
                .filter(
                  (tag: ITagView) =>
                    tag.parentId === tagId &&
                    !excludedTagChoices?.includes(Number(tag.id)) &&
                    !tag.deleted
                )
                .forEach((tag: ITagView) => {
                  childTags.push({
                    id: tag.id,
                    value: tag.text,
                    order: tag.order,
                  });
                });
              return childTags;
            },
            []
          )
        : [];

      list = [...tagChoices, ...parentTagChoices].sort(
        (a, b) => (a.order || (a.id as number)) - (b.order || (b.id as number))
      );
    } else {
      question?.choiceList?.forEach((choice: IChoiceView) => {
        list.push({ id: choice.value, value: choice.text });
      });
    }
    return list;
  };

  getChoiceId = (arrayName: string[]) => {
    if (arrayName[2]) {
      return arrayName[2];
    }
    return arrayName[1];
  };

  getChoice = (
    questionId: number,
    choiceOrTagId: string
  ): { id: number | string; title: string } => {
    const question = this.context.questions[questionId];
    if (
      question.type === 'multipleChoice' &&
      question.useTags &&
      !isNaN(Number(choiceOrTagId))
    ) {
      const tag = this.context.tags.data[Number(choiceOrTagId)];
      return { id: tag.id, title: tag.text };
    } else {
      const choice = (question as IMultipleChoiceQuestionView).choiceList.find(
        (ch: IChoiceView) => ch.value === choiceOrTagId
      );
      return { id: choice?.value || '', title: choice?.text || '' };
    }
  };
}

class MultipleAnswerQuestionFieldsBucket extends Bucket {
  extractResponse(category?: IAnalyticResponse['categories']): IAnswerItem[] {
    const ans: IAnswerItem[] = [];

    const { questionId, field } = this.queryBucket.customField;
    const questions = this.context.questions;
    const question = questions[questionId];
    const questionChoices = this.getQuestionFields(
      question as IMultipleAnswerQuestionView,
      field || ''
    );
    // if user choose a question choice in filters, we need to show just these choices
    if (this.queryBucket.includeTotal) {
      questionChoices.push({ id: totalKey, value: messages.total });
    }
    questionChoices.forEach((choice: IQuestionChoice) => {
      const item: IAnswerItem = {
        __name: choice.value,
        __value: this.queryMetric.metric === 'count' ? 0 : undefined,
        __id: choice.id,
      };
      if (
        this.queryBucket.subBuckets &&
        this.queryBucket.subBuckets.length > 0
      ) {
        const bucket = Bucket.bucketOf(
          this.queryBucket.subBuckets[0],
          this.queryMetric,
          this.queryFilters,
          this.analyse,
          this.series,
          this.context
        );
        let subCategory: IAnalyticResponse['categories'] | undefined;
        item.__value = bucket.extractResponse(subCategory); // add value for sub-bucket for default to show zero or undefined in chart
        if (category && category[this.queryBucket.name]) {
          const foundBucket = category[this.queryBucket.name].buckets.find(
            (b) =>
              b.val === choice.id ||
              b.val === `${this.queryBucket.prefix}$$${choice.id}`
          );
          if (foundBucket) {
            subCategory = foundBucket.categories;
            if (
              foundBucket.count === 0 &&
              this.queryMetric.metric !== 'count'
            ) {
              item.__value = undefined;
            } else {
              item.__value = bucket.extractResponse(subCategory) || 0;
            }
          }
        }
      } else {
        if (category && category[this.queryBucket.name].buckets) {
          const foundBucket = category[this.queryBucket.name].buckets.find(
            (bucket) =>
              bucket.val === choice.id ||
              bucket.val === `${this.queryBucket.prefix}$$${choice.id}`
          );
          if (foundBucket) {
            if (
              foundBucket.count === 0 &&
              this.queryMetric.metric !== 'count'
            ) {
              item.__value = undefined;
            } else {
              const { metric } = this.series;
              const metricScore = addScoreToMetric(metric);
              fillItemRawValues(item, foundBucket, metricScore, 0);
            }
          } else if (this.queryMetric.metric !== 'count') {
            item.__value = undefined;
          }
        }
      }
      ans.push(item);
    });
    return ans;
  }

  getQuestionFields = (
    question: IMultipleAnswerQuestionView,
    field: string
  ): IQuestionChoice[] => {
    const list: IQuestionChoice[] = [];
    (
      question?.questionFields?.find((it) => it.key === field)!.options || []
    ).forEach((option) => {
      list.push({
        id: option,
        value: option,
      });
    });
    return list;
  };
}

function scaleFactor(originalTimeScale: TimeScale, timeScale: TimeScale) {
  return relativeNumber(timeScale) / relativeNumber(originalTimeScale);
}

function relativeNumber(originalTimeScale: TimeScale) {
  switch (originalTimeScale) {
    case 'day':
      return 1;
    case 'week':
      return 7;
    case 'month':
      return 30;
    case 'season':
      return 90;
    case 'twoSeason':
      return 180;
    case 'year':
      return 360;
  }
}

class TimeBucket extends Bucket {
  shouldDiscard(
    // du to not part of report ( for previous years.
    jMonth: number, // end month is exclusive
    endJMonth: number,
    timeLength: number,
    gap: number
  ) {
    if (jMonth - 1 >= endJMonth - gap * timeLength - 1) return false;
    let currentLast = endJMonth - 1;
    while (currentLast >= jMonth) {
      if (jMonth <= currentLast && jMonth >= currentLast - gap + 1)
        return false;
      currentLast -= 12;
    }
    return true;
  }
  extractResponse(category?: IAnalyticResponse['categories']): IAnswerItem[] {
    const ans: IAnswerItem[] = [];
    if (category && category[this.queryBucket.name]) {
      const sortedCheckPoints = this.context.checkPoints.sort(
        (a, b) => (a.date || 0) - (b.date || 0)
      );
      let baseLineSum = 0; // used for total baseLine calc
      let targetSum = 0; // used for total target calc
      let itemCount = 0;
      category[this.queryBucket.name].buckets.forEach((resBucket) => {
        const dateName =
          resBucket.val === totalKey
            ? messages.total
            : this.generateTimeName(resBucket.val as number);
        const qb = this.queryBucket || {};
        if (
          qb.customField.timeLength &&
          this.shouldDiscard(
            Number(resBucket.val),
            qb.end!,
            qb.customField.timeLength,
            Number(qb.gap)
          )
        ) {
          return;
        }
        const timeStamp = resBucket.val;
        const checkPoint =
          timeStamp === totalKey
            ? ({
                baseLine:
                  baseLineSum /
                  (this.series.metric.endsWith('vg') ? itemCount || 1 : 1),
                target:
                  targetSum /
                  (this.series.metric.endsWith('vg') ? itemCount || 1 : 1),
              } as ICheckPoint)
            : sortedCheckPoints.find(
                (cp) =>
                  !!cp.date &&
                  (qb.field === 'effectiveDate'
                    ? cp.date >= Number(timeStamp)
                    : getJMonthIndex(
                        new Date(cp.date),
                        this.context.local || getCalendarLocal()
                      ) >= (timeStamp as number))
              );

        const item: IAnswerItem = {
          __name: dateName,
          __value: this.queryMetric.metric === 'count' ? 0 : undefined,
          __id: resBucket.val,
        };

        const scaledCheckpoint = checkPoint && cloneDeep(checkPoint);
        if (
          scaledCheckpoint &&
          qb.customField.originalTimeScale &&
          qb.customField.timeScale &&
          !this.series.metric.endsWith('vg') /*i.e is not average metric*/
        ) {
          const scale = scaleFactor(
            qb.customField.originalTimeScale,
            qb.customField.timeScale
          );
          scaledCheckpoint.baseLine! *= scale;
          scaledCheckpoint.target! *= scale;
          Object.values(
            scaledCheckpoint.properties?.distinctiveValues || {}
          ).forEach((value) => {
            if (value.baseLine) value.baseLine *= scale;
            if (value.target) value.target *= scale;
          });
        }
        this.queryFilters.forEach((it) => {
          if (it.type === 'termFilter' && it.values?.length === 1) {
            const value = it.values[0].toString().split('$$')?.[1];
            if (
              scaledCheckpoint?.properties?.distinctiveValues?.[value]?.target
            )
              scaledCheckpoint.target =
                scaledCheckpoint!.properties!.distinctiveValues![
                  value
                ]!.target!;
            if (
              scaledCheckpoint?.properties?.distinctiveValues?.[value]?.baseLine
            )
              scaledCheckpoint.baseLine =
                scaledCheckpoint!.properties!.distinctiveValues![
                  value
                ]!.baseLine!;
          }
        });
        if (
          this.queryBucket.subBuckets &&
          this.queryBucket.subBuckets.length > 0
        ) {
          const bucket = Bucket.bucketOf(
            this.queryBucket.subBuckets[0],
            this.queryMetric,
            this.queryFilters,
            this.analyse,
            this.series,
            this.context
          );
          let subCategory: IAnalyticResponse['categories'] | undefined;
          if (category && category[this.queryBucket.name]) {
            const foundBucket = category[this.queryBucket.name].buckets.find(
              (b) => b.val === resBucket.val
            );
            if (foundBucket) {
              subCategory = foundBucket.categories;
            }
          }
          const value = bucket.extractResponse(subCategory, scaledCheckpoint);
          item.__value = value;
          if (
            scaledCheckpoint &&
            isANumber(scaledCheckpoint.target) &&
            isANumber(scaledCheckpoint.baseLine)
          ) {
            const appliedTarget = scaledCheckpoint.target as number;
            const appliedBaseLine = scaledCheckpoint.baseLine as number;

            item.__target = appliedTarget;
            item.__baseLine = appliedBaseLine;
            if (typeof value === 'number') {
              item.__percentageOfRealization = calculatePercentage(
                value,
                item.__target,
                item.__baseLine
              );
              itemCount++;
              baseLineSum += item.__baseLine;
              targetSum += item.__target;
            } else {
              const subBucketValue = value.reduce((sum, valueItem) => {
                return sum + (valueItem.__value as number);
              }, 0);
              item.__percentageOfRealization = calculatePercentage(
                subBucketValue,
                item.__target,
                item.__baseLine
              );
              itemCount++;
              baseLineSum += item.__baseLine;
              targetSum += item.__target;
            }
          }
        } else {
          if (resBucket.count === 0 && this.queryMetric.metric !== 'count') {
            item.__value = undefined;
          } else {
            const { metric } = this.series;
            const metricScore = addScoreToMetric(metric);
            fillItemRawValues(item, resBucket, metricScore);
            if (
              scaledCheckpoint &&
              isANumber(scaledCheckpoint.target) &&
              isANumber(scaledCheckpoint.baseLine)
            ) {
              item.__target = scaledCheckpoint.target as number;
              item.__baseLine = scaledCheckpoint.baseLine as number;
              item.__percentageOfRealization = calculatePercentage(
                item.__value as number,
                item.__target,
                item.__baseLine
              );

              itemCount++;
              baseLineSum += item.__baseLine;
              targetSum += item.__target;
            }
          }
        }
        ans.push(item);
      });
    }

    return ans;
  }

  generateTimeName = (value: number): string => {
    const timestamp = this.getTimeStamp(value);
    let ret: string = '';
    const local = this.context.local || getCalendarLocal();
    const mnt = local === 'en' ? moment(value).locale('en') : moment(value);
    const mntTstp =
      local === 'en' ? moment(timestamp).locale('en') : moment(timestamp);
    const yearFormat = local === 'en' ? 'YYYY' : 'jYYYY';
    const monthFormat = local === 'en' ? 'MM' : 'jMM';
    const monthDayFormat = local === 'en' ? 'MMMM D' : 'jMMMM jD';
    const yearMonthFormat = local === 'en' ? 'MMMM YYYY' : 'jMMMM jYYYY';
    switch (this.context.timeScale) {
      case 'day':
        ret = mnt.format(monthDayFormat);
        break;
      case 'week':
        // eslint-disable-next-line no-case-declarations
        const firstOfWeek = mnt.startOf('week').format(monthDayFormat);
        // eslint-disable-next-line no-case-declarations
        const endOfWeek = mnt.endOf('week').format(monthDayFormat);
        ret = i18n.t('fromUntilTo', {
          ns: 'general',
          time1: firstOfWeek,
          time2: endOfWeek,
        }) as string;
        break;
      case 'month': {
        ret = mntTstp.format(yearMonthFormat);
        break;
      }
      case 'season': {
        const momentValue = mntTstp;
        const month = englishNumber(
          momentValue.add(30, 'day').format(monthFormat)
        );
        const monthNumber = Number(month);
        const year = momentValue.format(yearFormat);
        ret =
          local === 'en'
            ? messages.getEnSeason(monthNumber, year)
            : messages.getSeason(monthNumber, year);
        break;
      }
      case 'twoSeason': {
        const momentValue = mntTstp;
        const month = englishNumber(
          momentValue.add(30, 'day').format(monthFormat)
        );
        const monthNumber = Number(month);
        const year = momentValue.format(yearFormat);
        ret = messages.getHalfYearTitle(monthNumber, year);
        break;
      }
      case 'year':
        ret = mntTstp.add(182).format(yearFormat);
        break;
      default:
        ret = 'date';
        break;
    }
    return convertDigitToProperFormat(ret);
  };

  private getTimeStamp(value: number) {
    const local = (this.context.local || getCalendarLocal()) as ILocal;
    const isEffectiveJMonth = this.queryBucket.field === 'effectiveJMonth';
    const timestamp = isEffectiveJMonth
      ? monthIndexToTimestamp(value, local)
      : value;
    return timestamp;
  }
}

class AllQuestionsBucket extends Bucket {
  extractResponse(category?: IAnalyticResponse['categories']): IAnswerItem[] {
    const ans: IAnswerItem[] = [];

    const hasASeriesForAllOfQuestion = this.queryFilters.find(
      (filter) =>
        filter.field === 'questionId' &&
        filter.type === 'termFilter' &&
        !!filter.values &&
        (filter.values.length === 0 || filter.values.length > 1)
    );

    const iter: (IQuestionsView | string)[] = Array.from(
      Object.values(this.context.questions) as IQuestionsView[]
    ).sort((a: IQuestionsView, b: IQuestionsView) => a.order - b.order);

    if (this.queryBucket.includeTotal) {
      iter.push(totalKey);
    }
    iter.forEach((question: IQuestionsView | string) => {
      if (typeof question !== 'string' && question.deleted) return;
      if (
        !(
          hasASeriesForAllOfQuestion?.values?.length === 0 ||
          typeof question === 'string' ||
          (typeof question === 'object' &&
            (hasASeriesForAllOfQuestion?.values as number[]).includes(
              Number(question.id)
            ))
        )
      )
        return;
      let item: IAnswerItem;
      if (typeof question === 'string') {
        item = {
          __name: messages.total,
          __value: this.queryMetric.metric === 'count' ? 0 : undefined,
          __id: totalKey,
        };
      } else {
        const isInFields = this.context.fields.find(
          (field) =>
            field.type === 'question' && field.questionId === question.id
        );

        if (hasASeriesForAllOfQuestion && isInFields) {
          return;
        } /*else if (hasASeriesForAllOfQuestion && isScoredMultiChoiceQuestion) {
          return;
        }*/
        if (
          !(
            this.queryMetric.metric === 'count' ||
            question.type === 'indicator' ||
            (question as IMultipleChoiceQuestionView).choiceList?.find(
              (it) => it.score
            ) ||
            (question as IMultipleAnswerQuestionView).questionFields?.find(
              (it) => it.withValue
            )
          )
        )
          return;
        item = {
          __name: question.title,
          __value: this.queryMetric.metric === 'count' ? 0 : undefined,
          __id: question.id,
        };
      }

      if (
        this.queryBucket.subBuckets &&
        this.queryBucket.subBuckets.length > 0
      ) {
        const bucket = Bucket.bucketOf(
          this.queryBucket.subBuckets[0],
          this.queryMetric,
          this.queryFilters,
          this.analyse,
          this.series,
          this.context
        );
        let subCategory: IAnalyticResponse['categories'] | undefined;
        if (category && category[this.queryBucket.name]) {
          const foundBucket = category[this.queryBucket.name].buckets.find(
            (b) =>
              b.val ===
              (typeof question === 'string' ? question : question.title)
          );
          if (foundBucket) {
            subCategory = foundBucket.categories;
            if (
              foundBucket.count === 0 &&
              this.queryMetric.metric !== 'count'
            ) {
              item.__value = undefined;
            } else {
              item.__value = bucket.extractResponse(subCategory);
            }
          }
        }
      } else {
        if (category && category[this.queryBucket.name].buckets) {
          const foundBucket = category[this.queryBucket.name].buckets.find(
            (bucket) =>
              bucket.val ===
              (typeof question === 'string' ? question : question.id)
          );
          if (foundBucket) {
            const { metric } = this.series;
            const metricScore = addScoreToMetric(metric);
            if (typeof question === 'string') {
              fillItemRawValues(item, foundBucket, metricScore);
            } else if (
              foundBucket.count === 0 &&
              this.queryMetric.metric !== 'count'
            ) {
              item.__value = undefined;
            } else {
              fillItemRawValues(item, foundBucket, metricScore);
            }
          } else if (this.queryMetric.metric !== 'count') {
            item.__value = undefined;
          }
        }
      }
      ans.push(item);
    });
    return ans;
  }
}
class ServerTitledBucket extends Bucket {
  extractResponse(category?: IAnalyticResponse['categories']): IAnswerItem[] {
    const ans: IAnswerItem[] = [];

    const iter: { val: string; label: string }[] =
      category?.[this.queryBucket.name].buckets
        .map((it) => ({
          val: it.val.toString(),
          label: it.label || '',
        }))
        .sort((a, b) => smartNumericCompare(a.label, b.label)) || [];

    iter.forEach(({ val, label }) => {
      let item: IAnswerItem;
      if (val === totalKey) {
        item = {
          __name: messages.total,
          __value: this.queryMetric.metric === 'count' ? 0 : undefined,
          __id: totalKey,
        };
      } else {
        item = {
          __name: label,
          __value: this.queryMetric.metric === 'count' ? 0 : undefined,
          __id: val,
        };
      }

      if (
        this.queryBucket.subBuckets &&
        this.queryBucket.subBuckets.length > 0
      ) {
        const bucket = Bucket.bucketOf(
          this.queryBucket.subBuckets[0],
          this.queryMetric,
          this.queryFilters,
          this.analyse,
          this.series,
          this.context
        );
        let subCategory: IAnalyticResponse['categories'] | undefined;
        if (category && category[this.queryBucket.name]) {
          const foundBucket = category[this.queryBucket.name].buckets.find(
            (b) => b.val === val
          );
          if (foundBucket) {
            subCategory = foundBucket.categories;
            if (
              foundBucket.count === 0 &&
              this.queryMetric.metric !== 'count'
            ) {
              item.__value = undefined;
            } else {
              item.__value = bucket.extractResponse(subCategory);
            }
          }
        }
      } else {
        if (category && category[this.queryBucket.name].buckets) {
          const foundBucket = category[this.queryBucket.name].buckets.find(
            (bucket) => bucket.val === val
          );
          if (foundBucket) {
            const { metric } = this.series;
            const metricScore = addScoreToMetric(metric);
            if (val === totalKey) {
              fillItemRawValues(item, foundBucket, metricScore);
            } else if (
              foundBucket.count === 0 &&
              this.queryMetric.metric !== 'count'
            ) {
              item.__value = undefined;
            } else {
              fillItemRawValues(item, foundBucket, metricScore);
            }
          } else if (this.queryMetric.metric !== 'count') {
            item.__value = undefined;
          }
        }
      }
      ans.push(item);
    });
    return ans;
  }
}

class AllSubQuestionsBucket extends Bucket {
  extractResponse(category?: IAnalyticResponse['categories']): IAnswerItem[] {
    const ans: IAnswerItem[] = [];
    const questionId = this.queryBucket.customField as string;
    const question = this.context.questions[questionId] as IGridQuestionView;

    const parts = [...(question?.parts || [])];
    if (this.queryBucket.includeTotal) {
      parts.push({ text: messages.total, value: totalKey, score: 0 });
    }
    parts.forEach((choice: IChoiceView) => {
      const item: IAnswerItem = {
        __name: choice.text,
        __value: this.queryMetric.metric === 'count' ? 0 : undefined,
        __id: choice.value,
      };
      if (
        this.queryBucket.subBuckets &&
        this.queryBucket.subBuckets.length > 0
      ) {
        const bucket = Bucket.bucketOf(
          this.queryBucket.subBuckets[0],
          this.queryMetric,
          this.queryFilters,
          this.analyse,
          this.series,
          this.context
        );
        let subCategory: IAnalyticResponse['categories'] | undefined;
        if (category && category[this.queryBucket.name]) {
          const foundBucket = category[this.queryBucket.name].buckets.find(
            (b) => b.val === choice.value
          );
          if (foundBucket) {
            subCategory = foundBucket.categories;
            if (
              foundBucket.count === 0 &&
              this.queryMetric.metric !== 'count'
            ) {
              item.__value = undefined;
            } else {
              item.__value = bucket.extractResponse(subCategory);
            }
          }
        }
      } else {
        if (category && category[this.queryBucket.name].buckets) {
          const foundBucket = category[this.queryBucket.name].buckets.find(
            (bucket) => bucket.val === choice.value
          );
          if (foundBucket) {
            if (
              foundBucket.count === 0 &&
              this.queryMetric.metric !== 'count'
            ) {
              item.__value = undefined;
            } else {
              const { metric } = this.series;
              const metricScore = addScoreToMetric(metric);
              fillItemRawValues(item, foundBucket, metricScore);
            }
          } else if (this.queryMetric.metric !== 'count') {
            item.__value = undefined;
          }
        }
      }
      ans.push(item);
    });
    return ans;
  }
}

class InstanceBucket extends Bucket {
  generateUserName = (user: IUser | undefined, id: number) => {
    return user?.name || id;
  };
  generateProviderName = (provider: IProvider, id: number | string) => {
    if (provider) {
      if (provider.name) {
        return provider.name;
      } else if (provider.survey) {
        const questions = provider.survey.surveyQuestionList.filter(
          (question: IQuestionsView) => {
            if (question.tags) {
              return question.tags.find(
                (tag: ITagView) => tag.text === 'conf_include_in_name'
              );
            }
            return false;
          }
        );
        const responses = provider.infoSurveyResponse.surveyAnswerList;
        const providerName = responses.reduce((name: string, response: any) => {
          if (
            questions.find(
              (question: IQuestionsView) => question.id === response.questionId
            )
          ) {
            name = `${name} ${response.value}`;
          }
          return name;
        }, '');
        return providerName;
      }
      return `${messages.invalidProvider}(${id})`;
    }
    return `${messages.invalidProvider}(${id})`;
  };

  extractResponse(category?: IAnalyticResponse['categories']): IAnswerItem[] {
    let instantIds: Array<string | number> = [];
    const bucketName = this.queryBucket.name;
    if (this.queryBucket.validBuckets) {
      instantIds = this.queryBucket.validBuckets;
    } else if (category) {
      instantIds = category[bucketName].buckets.map((bucket) => bucket.val);
    }

    const ans: IAnswerItem[] = [];

    const iter = cloneDeep(instantIds).sort((a, b) => {
      if (!isANumber(a)) return 1;
      else if (!isANumber(b)) return -1;
      else return Number(a) - Number(b);
    });
    iter.forEach((id: number | string) => {
      const instantName =
        typeof id === 'string'
          ? messages.total
          : this.queryBucket.customField === 'Provider'
            ? this.generateProviderName(this.context.providers.map[id], id)
            : this.generateUserName(this.context.users?.[id], id);
      const item: IAnswerItem = {
        __name: instantName,
        __value: this.queryMetric.metric === 'count' ? 0 : undefined,
        __id: typeof id === 'string' ? totalKey : id,
      };
      if (
        this.queryBucket.subBuckets &&
        this.queryBucket.subBuckets.length > 0
      ) {
        const bucket = Bucket.bucketOf(
          this.queryBucket.subBuckets[0],
          this.queryMetric,
          this.queryFilters,
          this.analyse,
          this.series,
          this.context
        );
        let subCategory: IAnalyticResponse['categories'] | undefined;
        if (category && category[bucketName]) {
          const foundBucket = category[bucketName].buckets.find(
            (b) => b.val.toString() === id.toString()
          );
          if (foundBucket) {
            subCategory = foundBucket.categories;
            if (
              foundBucket.count === 0 &&
              this.queryMetric.metric !== 'count'
            ) {
              item.__value = undefined;
            } else {
              item.__value = bucket.extractResponse(subCategory);
            }
          }
        }
      } else {
        if (category && category[bucketName].buckets) {
          const foundBucket = category[bucketName].buckets.find(
            (bucket) => bucket.val.toString() === id.toString()
          );
          if (foundBucket) {
            if (
              foundBucket.count === 0 &&
              this.queryMetric.metric !== 'count'
            ) {
              item.__value = undefined;
            } else {
              const { metric } = this.series;
              const metricScore = addScoreToMetric(metric);
              fillItemRawValues(item, foundBucket, metricScore);
            }
          } else if (this.queryMetric.metric !== 'count') {
            item.__value = undefined;
          }
        }
      }
      ans.push(item);
    });

    return ans;
  }
}

function fillItemRawValues(
  item: IAnswerItem,
  foundBucket: IAnalyticResponse,
  metricScore: string,
  defaultValue?: number
) {
  item.__value = calculateValue(foundBucket.metrics[metricScore]);
  if (!isANumber(item.__value)) item.__value = defaultValue;
  item.__value__d = foundBucket.metrics[metricScore + '__d'];
  item.__value__n = foundBucket.metrics[metricScore + '__n'];
}

export function getJMonthIndex(date: Date, local: string) {
  const m = local === 'en' ? moment(date).locale('en') : moment(date);
  return local === 'en'
    ? m.year() * 12 + m.month()
    : m.jYear() * 12 + m.jMonth();
}
export default Bucket;
